import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { STALE_AFTER_HOURS_ON_LIST, formatCents, type StoreCode } from '@familyhub/shared';
import { db, type StoreRecord, type SuggestionRecord, storesByName } from '../db';
import { groupList, type DisplayItem } from '../list/grouping';
import { Tile } from '../components/Tile';
import { GearIcon } from '../components/GearIcon';
import {
  acceptSuggestion,
  adjustQuantity,
  checkOff,
  dismissSuggestion,
  moveToStore,
  removeItem,
  setNote,
  undoCheckOff,
} from '../list/actions';
import { type SyncState } from '../sync/engine';
import { Loading } from '../components/Loading';

/**
 * The list (ARCHITECTURE §6).
 *
 * Everything on this screen is read from IndexedDB, never from the network, so
 * it renders identically in a shop with no signal. The only thing the server
 * contributes is prices, which arrive with sync and carry their own age.
 *
 * **It is a grid of tiles rather than rows**, which is a change of shape and
 * not only of styling. A shopping list is scanned against a shelf, and the
 * thing that matches a shelf is the picture — at 40px in a row there was not
 * enough of it to match anything. Three columns put twelve items where eight
 * rows fitted and make the packshot nearly three times the size.
 *
 * The price left the list in the same change. A price answers *which of these
 * should I buy*, and by the time something is on the list that is settled; on a
 * 102px tile the number was competing with the name for the one glance a person
 * gives it. It moved into the item's sheet, beside the link to the comparison
 * view — which is where the question a price answers actually gets asked.
 *
 * Everything that is not "bought" now lives in that sheet: how many, which
 * shop, a note, and removing it. That is one tap further away than the old
 * inline controls, and deliberately so — ticking things off is what happens
 * twenty times a trip, and it is the only thing a *tap* on a tile does.
 *
 * The sheet is opened by **pressing and holding** a tile. It used to have a `⋯`
 * button in the corner of every tile, which meant a permanent control sitting
 * on top of the one thing the grid exists to show — the picture — for an action
 * taken once a week. The check mark that used to be drawn over each tile is
 * gone with it: it was a hint about what a tap does, and it turns out a
 * shopping list where tapping something takes it off the list does not need
 * one on every item forever.
 */

/** Where the sheet is anchored. */
type Sheet = { kind: 'details'; id: string };

export function ListScreen({ syncState }: { syncState: SyncState }) {
  const [filter, setFilter] = useState<StoreCode | null>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  /** The last thing ticked, offered back for a moment in case it was a mis-tap. */
  const [undoable, setUndoable] = useState<{ id: string; name: string } | null>(null);

  /**
   * Suggestions are ranked server-side from purchase history, which a phone
   * does not hold in full — but they are **cached** by sync from
   * the same `GET /list` that carries shelf prices, so this screen reads them
   * from Dexie like everything else.
   *
   * Two things that fixes: the same endpoint is no longer fetched twice per
   * render, and suggestions survive going offline. Planning a shop is when they
   * are most useful, and that happens at a kitchen table on bad wifi at least
   * as often as anywhere else.
   */
  const suggestions = useLiveQuery(
    async () => await db.suggestions.orderBy('confidence').reverse().toArray(),
    [],
    [] as SuggestionRecord[],
  );

  const data = useLiveQuery(async () => {
    const [items, generics, variants, stores, order, prices] = await Promise.all([
      db.list_items.toArray(),
      db.generic_products.toArray(),
      db.product_variants.toArray(),
      storesByName(),
      db.store_category_order.toArray(),
      db.prices.toArray(),
    ]);
    return { items, generics, variants, stores, order, prices };
  }, []);

  if (!data) return <Loading />;

  const groups = groupList({ ...data, staleAfterHours: STALE_AFTER_HOURS_ON_LIST }, filter);
  // Only stores this household actually has items at, plus the ones that exist:
  // the filter is for standing in a shop, so every real store belongs in it.
  const stores = data.stores;

  /**
   * The sheet holds an **id**, never the item, and re-resolves it every render.
   * A snapshot would go stale the moment the quantity stepper inside the sheet
   * did its job — the sheet would keep showing the number the item had when it
   * opened, which is precisely the number being changed.
   */
  const anchored = sheet
    ? (groups
        .flatMap((group) => group.items.map((item) => ({ item, storeId: group.storeId })))
        .find(({ item }) => item.id === sheet.id) ?? null)
    : null;

  async function tick(item: DisplayItem) {
    // Ticking is the whole gesture. An item with a variant carries its own
    // store; an Any item records a purchase with none. The household asked not
    // to be stopped for "where did you buy it?" on every check-off — frequency
    // still drives suggestions, it is only the per-shop attribution that goes.
    setSheet(null);
    await checkOff(item.id);
    setUndoable({ id: item.id, name: item.name });
    // Long enough to catch a mis-tap, short enough not to linger over the list
    // while somebody is shopping.
    window.setTimeout(
      () => setUndoable((current) => (current?.id === item.id ? null : current)),
      12_000,
    );
  }

  return (
    <>
      <header className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">‹ Hub</Link>
        <h1>Lijst</h1>
        {syncState === 'syncing' && <span className="badge">synchroniseren</span>}
        <Link className="badge" to="/boodschappen/activity">
          activiteit
        </Link>
        {/* ADAPTERS §5.3: adapter health belongs in the app, one tap from the
            list, not in a log file on the VPS. */}
        {/* The same cog as the hub and the agenda. Offline is not lost with the
            word gone — it rides on as an amber dot, and in the accessible name. */}
        <Link
          className={`badge badge-gear${syncState === 'offline' ? ' is-offline' : ''}`}
          to="/boodschappen/status"
          aria-label={syncState === 'offline' ? 'Status, offline' : 'Status'}
        >
          <GearIcon size={18} />
        </Link>
      </header>

      <nav className="chips" aria-label="Filteren op winkel">
        <button className="chip" aria-pressed={filter === null} onClick={() => setFilter(null)}>
          Alles
        </button>
        {/* A row of one chip is not a filter; say why rather than showing a
            control that cannot do anything. */}
        {stores.length === 0 && (
          <span className="row-sub">Winkels verschijnen na de eerste synchronisatie</span>
        )}
        {stores.map((s) => (
          <button
            key={s.id}
            className="chip"
            aria-pressed={filter === s.code}
            onClick={() => setFilter(s.code)}
          >
            {s.name}
          </button>
        ))}
      </nav>

      <main>
        {undoable && (
          <div className="banner">
            {undoable.name} afgevinkt
            <button
              className="btn btn-quiet"
              style={{ marginLeft: 'auto', minHeight: 32 }}
              onClick={() => {
                const target = undoable;
                setUndoable(null);
                void undoCheckOff(target.id);
              }}
            >
              Ongedaan maken
            </button>
          </div>
        )}

        {groups.length === 0 && (
          <p className="empty">Niets op de lijst.</p>
        )}

        {groups.map((group) => (
          <section className="group" key={group.storeId ?? 'any'}>
            <h2 className="group-head">
              {group.label}
              {/* A store whose adapter is down keeps its group, flagged, with
                  items and no prices — visible rather than vanished. */}
              {!group.adapterOk && <span className="flag">· geen live zoekresultaten</span>}
              {group.store && (
                // `?scope=list` opens the short view — only the categories on
                // the list for this store — because this link is tapped in a
                // shop, where hunting through the full taxonomy is the chore.
                <Link
                  className="badge"
                  style={{ marginLeft: 'auto' }}
                  to={`/boodschappen/aisles/${group.store}?scope=list`}
                >
                  schapvolgorde
                </Link>
              )}
            </h2>

            <div className="grid">
              {group.items.map((item) => (
                <Tile
                  key={item.id}
                  name={item.name}
                  sub={[item.detail, item.sizeLabel, item.note, item.suggestionReason]
                    .filter(Boolean)
                    .join(' · ')}
                  imagePath={item.imagePath}
                  organic={item.organic}
                  badge={item.quantity > 1 ? String(item.quantity) : null}
                  // The quantity is a badge, and a badge inside a button with an
                  // explicit label is never announced — so it is said here too,
                  // or a screen reader user is told to buy one of six things.
                  label={
                    item.quantity > 1
                      ? `${item.name} gekocht, ${item.quantity} stuks`
                      : `${item.name} gekocht`
                  }
                  onClick={() => void tick(item)}
                  // Press and hold, which is where the `⋯` on every tile went.
                  // The corner button below is the same action for anyone
                  // without a touchscreen; it is transparent until focused.
                  onLongPress={() => setSheet({ kind: 'details', id: item.id })}
                  corner={{
                    label: `Details van ${item.name}`,
                    glyph: '⋯',
                    onClick: () => setSheet({ kind: 'details', id: item.id }),
                  }}
                />
              ))}
            </div>
          </section>
        ))}

        {/* Below the list, never above it: the list is what the user came for.
            "The app suggests. It never adds anything itself." */}
        {suggestions.length > 0 && (
          <section className="group">
            <h2 className="group-head">Suggesties</h2>
            <div className="card">
              {suggestions.map((suggestion) => (
                <div className="row" key={suggestion.generic_product_id}>
                  <span className="row-main">
                    <span className="row-name">{suggestion.name}</span>
                    {/* Every suggestion shows why (ARCHITECTURE §3.2). */}
                    <span className="row-sub">{suggestion.reason}</span>
                  </span>
                  <button
                    className="btn btn-quiet"
                    onClick={() => void acceptSuggestion(suggestion)}
                  >
                    Toevoegen
                  </button>
                  <button
                    className="btn btn-quiet"
                    aria-label={`${suggestion.name} wegklikken`}
                    onClick={() => void dismissSuggestion(suggestion)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {anchored && sheet?.kind === 'details' && (
        <DetailSheet
          key={anchored.item.id}
          item={anchored.item}
          storeId={anchored.storeId}
          stores={stores}
          onClose={() => setSheet(null)}
        />
      )}

      <Link className="fab" to="/boodschappen/add">
        + Toevoegen
      </Link>
    </>
  );
}

/**
 * The shell every sheet shares.
 *
 * The grip is a real button rather than a decorative bar. Tapping outside works
 * too, but a sheet whose only exit is the backdrop has no exit at all for
 * anyone using a keyboard or a screen reader — and on a tall phone the sheet
 * covers most of what there is to tap outside of.
 */
function Sheet({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="sheet" role="dialog" aria-modal="true">
        <button className="sheet-grip" aria-label="Sluiten" onClick={onClose} />
        {children}
      </div>
    </>
  );
}

/**
 * Everything about one item that is not "bought".
 *
 * This is also where the price ended up when the tiles stopped carrying one,
 * and it is the right place for it: the number sits next to the link that opens
 * the full comparison, so seeing a price and asking whether it is a good one
 * are one gesture apart instead of on two unrelated screens.
 */
function DetailSheet({
  item,
  storeId,
  stores,
  onClose,
}: {
  item: DisplayItem;
  storeId: string | null;
  stores: StoreRecord[];
  onClose: () => void;
}) {
  const [note, setNoteText] = useState(item.note ?? '');

  return (
    <Sheet onClose={onClose}>
      <h2>{item.name}</h2>
      {(item.detail || item.sizeLabel) && (
        <p className="row-sub" style={{ margin: 0 }}>
          {[item.detail, item.sizeLabel].filter(Boolean).join(' · ')}
        </p>
      )}

      <div className="sheet-field">
        <span className="sheet-label">Aantal</span>
        <span className="qty">
          <button
            aria-label={`Eén ${item.name} minder`}
            onClick={() => void adjustQuantity(item.id, -1)}
          >
            −
          </button>
          <span>{item.quantity}</span>
          <button
            aria-label={`Eén ${item.name} meer`}
            onClick={() => void adjustQuantity(item.id, +1)}
          >
            +
          </button>
        </span>
      </div>

      <div className="sheet-field">
        <span className="sheet-label">Winkel</span>
        <div className="sheet-row">
          {/* Any is a first-class choice, not a fallback (ARCHITECTURE §6.2). */}
          <button
            className="chip"
            aria-pressed={storeId === null}
            aria-label={`${item.name} overal kopen`}
            onClick={() => void moveToStore(item.id, null)}
          >
            Overal
          </button>
          {stores.map((s) => (
            <button
              key={s.id}
              className="chip"
              aria-pressed={storeId === s.id}
              aria-label={`${item.name} verplaatsen naar ${s.name}`}
              onClick={() => void moveToStore(item.id, s.id)}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      <div className="sheet-field">
        <label className="sheet-label" htmlFor="item-note">
          Notitie
        </label>
        <input
          id="item-note"
          type="text"
          placeholder="bijv. de kleine verpakking"
          value={note}
          onChange={(e) => setNoteText(e.target.value)}
          // On blur rather than per keystroke: every change is a whole-row
          // write under per-row last-write-wins, and one per letter would be
          // twenty mutations in the outbox for one note.
          onBlur={() => void setNote(item.id, note)}
        />
      </div>

      <div className="sheet-row" style={{ justifyContent: 'space-between' }}>
        <span>
          {item.priceCents !== null ? (
            <span className={item.stale ? 'price stale' : 'price'}>
              {formatCents(item.priceCents)}
              {/* Never present an old price as current. */}
              {item.stale && <small>oude prijs</small>}
            </span>
          ) : (
            <span className="row-sub">Geen prijs bekend</span>
          )}
        </span>
        <Link className="btn" to={`/boodschappen/compare/${item.genericProductId}`} onClick={onClose}>
          Prijzen vergelijken
        </Link>
      </div>

      {/* Removing is deliberately not the same gesture as ticking: one means
          bought and teaches history, the other means "I don't want this after
          all" and teaches nothing. */}
      <button
        className="btn btn-danger"
        onClick={() => {
          onClose();
          void removeItem(item.id);
        }}
      >
        {item.name} van de lijst halen
      </button>
    </Sheet>
  );
}
